<template>
  <b-modal
      :visible="visible"
      id="admin-new-user-modal"
      ref="modal"
      title="Add new User"
      hide-footer
      centered
  >
    <b-form @submit.prevent="submitForm">
      <b-form-group label="First Name">
        <b-form-input
            v-model.trim="formData.first_name"
            type="text"
            :state="fieldValid('first_name')"
        />
        <b-form-invalid-feedback :state="fieldValid('first_name')">
          {{ formValidation.first_name }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Last Name">
        <b-form-input
            v-model.trim="formData.last_name"
            type="text"
            :state="fieldValid('last_name')"
        />
        <b-form-invalid-feedback :state="fieldValid('last_name')">
          {{ formValidation.last_name }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Login">
        <b-form-input
            v-model.trim="formData.login"
            type="text"
            :state="fieldValid('login')"
        />
        <b-form-invalid-feedback :state="fieldValid('login')">
          {{ formValidation.login }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Email">
        <b-form-input
            v-model.trim="formData.email"
            type="text"
            :state="fieldValid('email')"
        />
        <b-form-invalid-feedback :state="fieldValid('email')">
          {{ formValidation.email }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Role">
        <b-form-select
            v-model.trim="formData.role"
            :options="availableRoles"
            :state="fieldValid('role')"
        />
        <b-form-invalid-feedback :state="fieldValid('role')">
          {{ formValidation.role }}
        </b-form-invalid-feedback>
      </b-form-group>
      <transition name="slide">
        <div v-if="formData.role !== 'admin'">
          <b-form-group label="Company">
            <b-form-select
                v-model.trim="formData.company"
                :options="companies"
                :state="fieldValid('company')"
            />
            <b-form-invalid-feedback :state="fieldValid('company')">
              {{ formValidation.company }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="User License">
            <b-form-input
                v-model.trim="formData.user_license"
                type="text"
                :state="fieldValid('user_license')"
            />
            <b-form-invalid-feedback :state="fieldValid('user_license')">
              {{ formValidation.user_license }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="METRC API Key">
            <b-form-input
                v-model.trim="formData.metrc_api_key"
                type="text"
                :state="fieldValid('metrc_api_key')"
            />
            <b-form-invalid-feedback :state="fieldValid('metrc_api_key')">
              {{ formValidation.metrc_api_key }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </transition>
      <b-form-group label="Password">
        <b-form-input
            v-model.trim="formData.password"
            type="password"
            autocomplete="new-password"
            :state="fieldValid('password')"
        />
        <b-form-invalid-feedback :state="fieldValid('password')">
          {{ formValidation.password }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Confirm Password">
        <b-form-input
            v-model.trim="formData.password_confirmation"
            type="password"
            autocomplete="new-password"
        />
      </b-form-group>
      <b-button type="submit" variant="primary" :disabled="formState.processing">
        Save new User
        <b-spinner v-if="formState.processing" class="ml-1" small></b-spinner>
      </b-button>
      <b-btn type="button" variant="secondary" @click.prevent="close">Cancel</b-btn>
    </b-form>
  </b-modal>
</template>

<script>
import {API_ROUTES} from "../api";

export default {
  name: "AdminNewUserModal",
  emits: ['created'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    skipAdminRole: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: Number
    }
  },
  data() {
    return {
      companies: [],
      formData: {
        first_name: "",
        last_name: "",
        login: "",
        email: "",
        role: "",
        company: this.companyId ?? "",
        user_license: "",
        metrc_api_key: "",
        is_active: "",
        password: "",
        password_confirmation: ""
      },
      formValidation: {},
      formState: {
        processing: false
      }
    }
  },
  computed: {
    availableRoles() {
      let roles = ['owner', 'staff'];
      if (!this.skipAdminRole) {
        roles.push('admin');
      }
      return roles;
    }
  },
  methods: {
    open() {
      this.$root.$emit('bv::show::modal', 'admin-new-user-modal');
    },
    close() {
      this.$root.$emit('bv::hide::modal', 'admin-new-user-modal');
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    submitForm() {
      this.formState.processing = true;
      this.$api.post(`${API_ROUTES.ADMIN_USERS}`, this.formData).then(({data}) => {
        this.$bvToast.toast('User has been successfully created', {
          title: 'Success!',
          variant: 'success'
        });
        this.formValidation = {};
        this.$emit('created', data.user);
        this.formData = {
          first_name: "",
          last_name: "",
          login: "",
          email: "",
          role: "",
          company: "",
          is_active: "",
          password: "",
          password_confirmation: ""
        };
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to create a new user', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  },
  created() {
    this.$api.get(API_ROUTES.ADMIN_COMPANIES, {params: {sorting: "name_asc"}}).then(({data}) => {
      this.companies = data.companies.map(company => ({
        value: company.id,
        text: company.name
      }));
    });
  }
}
</script>
